/**
 * Grupos de propriedades que se repetem entre campos.
 *
 * Não é um "helper genérico": cada função aqui monta UM grupo específico e
 * nomeado, e o descriptor continua declarando explicitamente quais grupos usa e
 * em que ordem. A alternativa — repetir os mesmos 4 blocos em 15 descriptors —
 * não seria mais legível, seria só mais longa e mais fácil de dessincronizar.
 */
import { PropertyGroup, PropertySchema } from '../property-schema';

/**
 * Identificação do campo: rótulo, dica e ajuda.
 * É o primeiro grupo de todo campo de formulário, e o único que o modo simples
 * mostra por inteiro.
 */
export function buildIdentityGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.inspector.groups.essential.title',
    level: 'simple',
    properties: [
      {
        kind: 'text',
        path: 'labelKey',
        labelKey: 'builderScreen.inspector.label',
        helpKey: 'builderScreen.inspector.label.help',
        level: 'simple'
      },
      {
        kind: 'text',
        path: 'placeholderKey',
        labelKey: 'builderScreen.inspector.placeholder',
        helpKey: 'builderScreen.inspector.placeholder.help',
        level: 'simple'
      },
      {
        kind: 'text',
        path: 'helpKey',
        labelKey: 'builderScreen.inspector.guide.title',
        level: 'advanced'
      }
    ]
  };
}

/**
 * Estado do campo: obrigatório, somente leitura, oculto.
 * O condicional (`requiredWhen`, `visibleWhen`) mora em `behavior` e é configurado
 * na aba de comportamento — aqui é só o estado estático.
 */
export function buildStateGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.inspector.groups.validation.title',
    level: 'simple',
    properties: [
      {
        kind: 'boolean',
        path: 'required',
        labelKey: 'builderScreen.inspector.required',
        helpKey: 'builderScreen.inspector.required.help',
        level: 'simple'
      },
      {
        kind: 'boolean',
        path: 'readonly',
        labelKey: 'builderScreen.inspector.isDisabled',
        helpKey: 'builderScreen.inspector.isDisabled.help',
        level: 'simple'
      },
      {
        kind: 'boolean',
        path: 'hidden',
        labelKey: 'builderScreen.inspector.hidden',
        level: 'advanced'
      }
    ]
  };
}

/**
 * Ligação com os dados: caminho no JSON e mapeamento no banco.
 *
 * No modo simples o usuário escolhe o caminho numa árvore do JSON de amostra e
 * nunca vê `dataPath` cru; entidade e coluna são exclusivas do modo avançado.
 */
export function buildDataGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.inspector.dataSection',
    level: 'simple',
    properties: [
      {
        kind: 'dataPath',
        path: 'dataPath',
        target: 'binding',
        labelKey: 'builderScreen.inspector.dataPath',
        helpKey: 'builderScreen.inspector.dataPath.help',
        placeholderKey: 'builderScreen.inspector.placeholder.dataPath',
        level: 'simple'
      },
      {
        kind: 'text',
        path: 'entityName',
        target: 'binding',
        labelKey: 'builderScreen.inspector.entityName',
        helpKey: 'builderScreen.inspector.entityName.help',
        placeholderKey: 'builderScreen.inspector.entityName.placeholder',
        level: 'advanced'
      },
      {
        kind: 'text',
        path: 'fieldName',
        target: 'binding',
        labelKey: 'builderScreen.inspector.fieldName',
        helpKey: 'builderScreen.inspector.fieldName.help',
        placeholderKey: 'builderScreen.inspector.fieldName.placeholder',
        level: 'advanced'
      },
      {
        kind: 'text',
        path: 'transform',
        target: 'binding',
        labelKey: 'builderScreen.inspector.formatter',
        level: 'advanced'
      }
    ]
  };
}

/**
 * Disposição: largura e altura na grade de 24 colunas.
 *
 * No modo simples a largura aparece como atalhos (pequeno / médio / linha inteira),
 * não como número — por isso o `kind: 'width'`, que tem editor próprio.
 */
export function buildLayoutGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.inspector.groups.layout.title',
    level: 'simple',
    properties: [
      {
        kind: 'width',
        path: 'colSpan',
        target: 'layout',
        labelKey: 'builderScreen.inspector.colspan',
        helpKey: 'builderScreen.inspector.colspan.help',
        level: 'simple'
      },
      {
        kind: 'number',
        path: 'rowSpan',
        target: 'layout',
        labelKey: 'builderScreen.inspector.rowspan',
        helpKey: 'builderScreen.inspector.rowspan.help',
        level: 'advanced',
        min: 1,
        max: 12
      },
      {
        kind: 'number',
        path: 'order',
        target: 'layout',
        labelKey: 'builderScreen.panel.properties.order',
        helpKey: 'builderScreen.inspector.orderHelp',
        level: 'advanced',
        min: 0
      }
    ]
  };
}

/**
 * Aparência por token. Substitui o seletor de cor livre, que era o furo pelo qual
 * o usuário quebrava o tema com a própria mão.
 */
export function buildAppearanceGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.inspector.groups.appearance.title',
    level: 'advanced',
    collapsed: true,
    properties: [
      {
        kind: 'token',
        path: 'emphasis',
        target: 'style',
        tokenSet: 'emphasis',
        labelKey: 'builderScreen.inspector.color',
        helpKey: 'builderScreen.inspector.color.help',
        level: 'advanced'
      }
    ]
  };
}

/**
 * Dependências e mapeamentos entre campos.
 * Usa os editores que já existem e funcionam — entram como `custom`, sem reescrita.
 */
export function buildDependencyGroup(): PropertyGroup {
  return {
    labelKey: 'builderScreen.searchDependency.title',
    level: 'advanced',
    collapsed: true,
    properties: [
      {
        kind: 'custom',
        path: 'dependencies',
        target: 'binding',
        editor: 'searchDependencies',
        labelKey: 'builderScreen.searchDependency.title',
        helpKey: 'builderScreen.inspector.dependencyHelp',
        level: 'advanced'
      },
      {
        kind: 'custom',
        path: 'mappings',
        target: 'binding',
        editor: 'fieldMappings',
        labelKey: 'builderScreen.fieldMapping.targetField',
        level: 'advanced'
      }
    ]
  };
}

/** Os quatro grupos que praticamente todo campo de formulário tem, na ordem padrão. */
export function buildStandardFieldGroups(specific: readonly PropertySchema[] = []): PropertyGroup[] {
  const groups: PropertyGroup[] = [buildIdentityGroup()];

  if (specific.length > 0) {
    groups.push({
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: specific
    });
  }

  groups.push(buildStateGroup());
  groups.push(buildDataGroup());
  groups.push(buildLayoutGroup());
  groups.push(buildAppearanceGroup());

  return groups;
}
